import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Textarea, Badge } from '@jaylabs/ui';
import Logo from "../components/Logo";
import AppFooter from "../components/AppFooter";
import UserBadgeBar from '../components/UserBadgeBar';
import { showSuccessToast, showErrorToast } from '../hooks/useToast';
import { API_URL } from '../utils/apiUrl';
import './Projects.css';

function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectTasks, setProjectTasks] = useState({});
  const [expandedProjects, setExpandedProjects] = useState({});
  const storedBg = localStorage.getItem('dashboardBg') || '/backgrounds/Background-2-1024x682.jpg';

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#8B5CF6'
  });

  const colorOptions = [
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Green', value: '#10B981' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Yellow', value: '#F59E0B' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Teal', value: '#14B8A6' }
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        setProjects(data.projects || []);
      } else {
        console.error('Failed to fetch projects:', data);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        showSuccessToast('Project created!', 'Your project has been created successfully.');
        setShowCreateForm(false);
        setFormData({ name: '', description: '', color: '#8B5CF6' });
        fetchProjects();
      } else {
        const data = await response.json();
        showErrorToast('Failed to create project', data.message || 'An error occurred.');
      }
    } catch (err) {
      showErrorToast('Error', 'Failed to connect to server.');
      console.error('Error:', err);
    }
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/projects/${editingProject.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        showSuccessToast('Project updated!', 'Your changes have been saved.');
        setEditingProject(null);
        setFormData({ name: '', description: '', color: '#8B5CF6' });
        fetchProjects();
      } else {
        const data = await response.json();
        showErrorToast('Failed to update project', data.message || 'An error occurred.');
      }
    } catch (err) {
      showErrorToast('Error', 'Failed to connect to server.');
      console.error('Error:', err);
    }
  };

  const handleArchiveProject = async (projectId) => {
    if (!window.confirm('Archive this project? You can restore it later.')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_archived: true })
      });
      if (response.ok) {
        showSuccessToast('Project archived', 'The project has been archived.');
        fetchProjects();
      }
    } catch (err) {
      showErrorToast('Error', 'Failed to archive project.');
      console.error('Error:', err);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Delete this project permanently? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        showSuccessToast('Project deleted', 'The project has been permanently deleted.');
        fetchProjects();
      }
    } catch (err) {
      showErrorToast('Error', 'Failed to delete project.');
      console.error('Error:', err);
    }
  };

  const startEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      color: project.color || '#8B5CF6'
    });
    setShowCreateForm(false);
  };

  const toggleProjectTasks = async (projectId) => {
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));

    if (!expandedProjects[projectId] && !projectTasks[projectId]) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/tasks?project_id=${projectId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (response.ok) {
          setProjectTasks(prev => ({ ...prev, [projectId]: data.tasks || [] }));
        }
      } catch (err) {
        console.error('Error fetching project tasks:', err);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      className="dd-projects-page"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0.7)), url(${storedBg})`
      }}
    >
      <div className="dd-container" style={{ paddingTop: '4rem' }}>
        {/* Top Bar */}
        <div className="dd-hstack dd-mb-6" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Logo size="xxl" />
          <UserBadgeBar />
        </div>

        {/* Header */}
        <div className="dd-hstack dd-mb-8" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--dd-space-4)' }}>
          <h1 className="dd-heading-xl">📁 Projects</h1>
          <div className="dd-hstack dd-gap-4">
            <Button variant="outline" size="lg" onClick={() => navigate('/dashboard')}>
              Dashboard
            </Button>
            <Button
              variant="success"
              size="lg"
              onClick={() => {
                setShowCreateForm(!showCreateForm);
                setEditingProject(null);
                setFormData({ name: '', description: '', color: '#8B5CF6' });
              }}
            >
              {showCreateForm ? 'Cancel' : '+ Create Project'}
            </Button>
          </div>
        </div>

        {/* Create/Edit Form */}
        {(showCreateForm || editingProject) && (
          <div className="dd-card dd-mb-8">
            <h3 className="dd-heading-md dd-mb-4">
              {editingProject ? 'Edit Project' : 'Create New Project'}
            </h3>
            <form onSubmit={editingProject ? handleUpdateProject : handleCreateProject}>
              <div className="dd-vstack dd-gap-4">
                <div className="dd-form-field">
                  <label className="dd-label">Project Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter project name"
                    required
                    size="lg"
                  />
                </div>

                <div className="dd-form-field">
                  <label className="dd-label">Description</label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter project description (optional)"
                    rows={3}
                  />
                </div>

                <div>
                  <label className="dd-label dd-mb-2">Color</label>
                  <div className="dd-hstack dd-gap-3 dd-hstack-wrap">
                    {colorOptions.map((color) => (
                      <div
                        key={color.value}
                        className={`dd-color-swatch ${formData.color === color.value ? 'dd-color-swatch-selected' : ''}`}
                        style={{ backgroundColor: color.value }}
                        onClick={() => setFormData({ ...formData, color: color.value })}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>

                <div className="dd-hstack dd-gap-3">
                  <Button type="submit" variant="success" size="lg">
                    {editingProject ? 'Update Project' : 'Create Project'}
                  </Button>
                  {editingProject && (
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => {
                        setEditingProject(null);
                        setFormData({ name: '', description: '', color: '#8B5CF6' });
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Projects List */}
        {loading ? (
          <div className="dd-text-center dd-py-8">
            <div className="dd-spinner dd-spinner-xl"></div>
            <p className="dd-text-lg dd-text-secondary dd-mt-4">Loading projects...</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="dd-card dd-text-center" style={{ padding: 'var(--dd-space-12)' }}>
            <p className="dd-text-lg dd-text-secondary dd-mb-4">No active projects</p>
            <p className="dd-text-secondary dd-mb-6">
              Create a new project to organize your tasks! (Note: Archived projects are hidden by default)
            </p>
            <Button variant="success" size="lg" onClick={() => setShowCreateForm(true)}>
              + Create Your First Project
            </Button>
          </div>
        ) : (
          <div className="dd-projects-grid">
            {projects.map((project) => (
              <div
                key={project.id}
                className="dd-project-card"
                style={{ borderColor: project.color || 'var(--dd-border)' }}
              >
                <div className="dd-vstack dd-gap-4">
                  <div className="dd-hstack" style={{ justifyContent: 'space-between' }}>
                    <div
                      className="dd-project-color-dot"
                      style={{ backgroundColor: project.color || '#8B5CF6' }}
                    />
                    {project.is_archived && (
                      <Badge variant="secondary">Archived</Badge>
                    )}
                  </div>

                  <div>
                    <h3 className="dd-heading-md dd-mb-2">{project.name}</h3>
                    {project.description && (
                      <p className="dd-text-sm dd-text-secondary">{project.description}</p>
                    )}
                  </div>

                  <div className="dd-hstack dd-gap-3 dd-hstack-wrap">
                    <Badge variant="info">{project.task_count || 0} tasks</Badge>
                    <Badge variant="success">{project.completed_count || 0} done</Badge>
                  </div>

                  <div className="dd-vstack dd-gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      className="dd-full-width"
                      onClick={() => toggleProjectTasks(project.id)}
                    >
                      {expandedProjects[project.id] ? '▲ Hide Tasks' : '▼ Show Tasks'}
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      className="dd-full-width"
                      onClick={() => startEdit(project)}
                    >
                      ✏️ Edit
                    </Button>
                    <div className="dd-hstack dd-gap-2">
                      {!project.is_archived && (
                        <Button
                          variant="outline"
                          size="sm"
                          style={{ flex: 1 }}
                          onClick={() => handleArchiveProject(project.id)}
                        >
                          📦 Archive
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="sm"
                        style={{ flex: 1 }}
                        onClick={() => handleDeleteProject(project.id)}
                      >
                        🗑️ Delete
                      </Button>
                    </div>
                  </div>

                  {/* Expanded tasks */}
                  {expandedProjects[project.id] && (
                    <div style={{ marginTop: 'var(--dd-space-4)', paddingTop: 'var(--dd-space-4)', borderTop: '1px solid var(--dd-border)' }}>
                      <h4 className="dd-text-sm dd-text-semibold dd-mb-3">Related Tasks</h4>
                      {projectTasks[project.id]?.length === 0 ? (
                        <p className="dd-text-sm dd-text-secondary dd-text-center dd-py-2">
                          No tasks in this project yet
                        </p>
                      ) : (
                        <div className="dd-vstack dd-gap-2">
                          {projectTasks[project.id]?.map((task) => (
                            <div
                              key={task.id}
                              className={`dd-task-item ${task.is_completed ? 'dd-task-item-complete' : 'dd-task-item-pending'}`}
                            >
                              <div className="dd-vstack dd-gap-1">
                                <span
                                  className="dd-text-sm dd-text-semibold"
                                  style={{
                                    textDecoration: task.is_completed ? 'line-through' : 'none',
                                    opacity: task.is_completed ? 0.6 : 1
                                  }}
                                >
                                  {task.title}
                                </span>
                                <div className="dd-hstack dd-gap-2 dd-hstack-wrap">
                                  <Badge variant="primary" size="sm">{task.category}</Badge>
                                  <Badge variant="info" size="sm">{task.points} pts</Badge>
                                  {task.due_date && (
                                    <Badge variant="warning" size="sm">{formatDate(task.due_date)}</Badge>
                                  )}
                                  {task.is_completed && (
                                    <Badge variant="success" size="sm">✓ Done</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AppFooter />
    </div>
  );
}

export default Projects;
